import Apertavel from '@/components/movimento/Apertavel';

export type TelaCampista = 'pessoas' | 'vagas' | 'tribos' | 'presencial' | 'relatorios';

interface Props {
  comVaga: number;
  suplentes: number;
  grupos: number;
  /** Vagas livres que estão com alerta de suplente esperando. */
  vagasLivresAlerta: number;
  /** Só aparece o cartão quando a participação é presencial. */
  temPresencial: boolean;
  aguardandoPresencial: number;
  aoAbrir: (tela: TelaCampista) => void;
}

const CORES: Record<'primary' | 'accent' | 'secondary', string> = {
  primary: 'bg-primary-100 text-primary-700',
  accent: 'bg-accent-100 text-accent-900',
  secondary: 'bg-secondary-100 text-secondary-900',
};

interface Cartao {
  tela: TelaCampista;
  icone: string;
  titulo: string;
  descricao: string;
  numero: string;
  legenda: string;
  cor: 'primary' | 'accent' | 'secondary';
  alerta?: string;
}

// Hub da aba Campistas: um cartão grande por função, sem subabas.
export default function HubCampistas({
  comVaga,
  suplentes,
  grupos,
  vagasLivresAlerta,
  temPresencial,
  aguardandoPresencial,
  aoAbrir,
}: Props) {
  // O número grande é SEMPRE quem tem vaga; suplentes só aparecem na descrição.
  const descricaoPessoas =
    suplentes > 0
      ? `${comVaga} com vaga · ${suplentes} ${suplentes === 1 ? 'suplente' : 'suplentes'}`
      : `${comVaga} com vaga`;

  const cartoes: Cartao[] = [
    {
      tela: 'pessoas',
      icone: 'ri-group-line',
      titulo: 'Pessoas',
      descricao: descricaoPessoas,
      numero: String(comVaga),
      legenda: 'com vaga',
      cor: 'primary',
    },
    {
      tela: 'vagas',
      icone: 'ri-ticket-2-line',
      titulo: 'Vagas e suplentes',
      descricao: vagasLivresAlerta > 0 ? '' : 'Fila de espera, senhas e alertas',
      numero:
        vagasLivresAlerta > 0 ? String(vagasLivresAlerta) : String(grupos),
      legenda:
        vagasLivresAlerta > 0
          ? vagasLivresAlerta === 1
            ? 'vaga livre'
            : 'vagas livres'
          : grupos === 1
            ? 'grupo'
            : 'grupos',
      cor: 'accent',
      alerta:
        vagasLivresAlerta > 0
          ? `${vagasLivresAlerta} vaga${vagasLivresAlerta === 1 ? '' : 's'} livre${
              vagasLivresAlerta === 1 ? '' : 's'
            } com suplentes esperando`
          : undefined,
    },
    {
      tela: 'tribos',
      icone: 'ri-flag-2-line',
      titulo: 'Tribos',
      descricao: 'Quem está em cada tribo',
      numero: String(grupos),
      legenda: grupos === 1 ? 'grupo' : 'grupos',
      cor: 'secondary',
    },
  ];

  if (temPresencial) {
    cartoes.push({
      tela: 'presencial',
      icone: 'ri-qr-scan-2-line',
      titulo: 'Inscrição presencial',
      descricao: 'Fila por senha e leitor de QR',
      numero: String(aguardandoPresencial),
      legenda: 'aguardando',
      cor: 'secondary',
    });
  }

  cartoes.push({
    tela: 'relatorios',
    icone: 'ri-file-list-3-line',
    titulo: 'Relatórios dos campistas',
    descricao: descricaoPessoas,
    numero: String(comVaga),
    legenda: 'com vaga',
    cor: 'primary',
  });

  return (
    <div className="space-y-2.5 lg:grid lg:grid-cols-3 lg:gap-3 lg:space-y-0">
      {cartoes.map((c) => (
        <Apertavel
          key={c.tela}
          onClick={() => aoAbrir(c.tela)}
          className="flex min-h-[84px] w-full cursor-pointer items-center gap-3 rounded-3xl border border-background-200 bg-white px-4 py-3.5 text-left transition-colors hover:border-primary-200 hover:bg-primary-50/40"
        >
          <span
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-xl ${CORES[c.cor]}`}
          >
            <i className={c.icone} aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[15px] font-bold text-foreground-950">
              {c.titulo}
            </span>
            {c.alerta ? (
              <span className="mt-0.5 flex items-center gap-1.5 text-[12.5px] font-semibold text-amber-800">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-alert-line" aria-hidden />
                </span>
                {c.alerta}
              </span>
            ) : (
              <span className="mt-0.5 block text-[12px] leading-snug text-foreground-600">
                {c.descricao}
              </span>
            )}
          </span>
          <span className="shrink-0 text-right">
            <span
              className={`block text-[18px] font-extrabold leading-none tabular-nums ${
                c.alerta ? 'text-amber-800' : 'text-foreground-950'
              }`}
            >
              {c.numero}
            </span>
            <span className="mt-1 block text-[11px] font-medium text-foreground-500">
              {c.legenda}
            </span>
          </span>
          <span className="flex h-6 w-6 shrink-0 items-center justify-center text-foreground-400">
            <i className="ri-arrow-right-s-line text-lg" aria-hidden />
          </span>
        </Apertavel>
      ))}
    </div>
  );
}